import React, { useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  params, string, shape, number, client, extend, useDataModel, arrayOf
} from '@thd-nucleus/data-sources';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { DrawerHeader } from '@one-thd/sui-atomic-components';
import { AddToContext } from '../../AddToContext';
import {
  useListDetails, useUpdateList, listDetailsModel, updateListModel
} from '../../hooks/useLists';
import { useStorefrontData } from '../../hooks/useStorefrontData';
import { SCREENS, OPERATION_ADD_ITEMS } from '../../AddToConstants';
import { SignIn } from '../../common/addto-signin/SignIn';
import { CreateList } from './CreateList';
import { SelectList } from './SelectList';
import { AddToListSuccess } from './AddToListSuccess';
import { AddToLoading } from '../../common/addto-loading/AddToloading';
import { DefaultScreen } from '../../common/DefaultScreen';
import { MiniPip } from '../../common/mini-pip/MiniPip';
import { CopyList } from '../copy-list/CopyList';

// Screen title mapping
const SCREEN_TITLES = {
  [SCREENS.LIST_CREATE_LIST]: 'Create List',
  [SCREENS.LIST_SELECT_LIST]: 'Select List',
  [SCREENS.DEFAULT]: 'Add Materials',
  [SCREENS.MINI_PIP_PRODUCT_REVIEWS]: 'Add Materials',
  [SCREENS.MINI_PIP_PRODUCT_SPECS]: 'Add Materials',
  [SCREENS.MINI_PIP_PRODUCT_OVERVIEW]: 'Add Materials',
  [SCREENS.MINI_PIP_BASE]: 'Add Materials',
  [SCREENS.COPY_LIST_SUMMARY]: 'Copy from Your Lists',
  [SCREENS.COPY_LIST_PRODUCTS]: 'Copy from Your Lists',
};

export const AddToList = (props) => {
  const {
    products, skipListId, isSharedList, listId, onMaterialsAdded
  } = props;

  const theme = useTheme(AddToList, props);
  const { disableSignInDrawer } = theme.props;

  const {
    addToType,
    storeId,
    setScreen,
    screen,
    handleClose,
    selectedProductDetails,
    setShowSuccessMessage,
    setShowErrorMessage,
    atcDrawerType,
    setAtcDrawerType
  } = useContext(AddToContext);
  const { svocID, userID, customerType, isLoggedIn } = useThdCustomer() || {};
  const [isSearchResults, setIsSearchResults] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [newListDetails, setNewListDetails] = useState({
    listName: '', listId: ''
  });
  const [keyword, setKeyword] = useState('');
  const {
    LIST_CREATE_LIST,
    LIST_SELECT_LIST,
    SIGN_IN,
    LIST_SUCCESS,
    LOADING,
    DEFAULT,
    MINI_PIP_BASE,
    MINI_PIP_PRODUCT_OVERVIEW,
    MINI_PIP_PRODUCT_SPECS,
    MINI_PIP_PRODUCT_REVIEWS,
    MINI_PIP_FULL
  } = SCREENS;
  const { storeFrontSVOCID, storeFrontUserID } = useStorefrontData();
  const itemId = selectedProductDetails?.itemId || products[0]?.itemId;

  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId || products?.length > 1
  });

  const productData = data?.product || {};
  const productValue = productData?.pricing?.value;
  const identifiers = productData?.identifiers;
  const primaryImage = productData?.media?.images?.find(
    (img) => img.type === 'IMAGE' && img.subType === 'PRIMARY'
  );

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('AddToListDrawer.ready');
  }, []);

  const canSkipListDetails = useMemo(() => {
    if (products?.length === 0) {
      return true;
    }
    return storeFrontSVOCID
      // eslint-disable-next-line max-len
      ? ((!storeFrontSVOCID || !storeFrontUserID) || addToType !== 'List') : ((!svocID || !userID) || addToType !== 'List' && atcDrawerType !== 'List')
  }, [products?.length, storeFrontSVOCID, storeFrontUserID, svocID, userID, addToType]);

  const { listData, listLoading, listRefetch } = useListDetails({
    customerAccountID: storeFrontSVOCID || svocID,
    userId: storeFrontSVOCID ? storeFrontUserID : userID,
    customerType: storeFrontSVOCID ? null : customerType?.toUpperCase(),
    skip: canSkipListDetails
  });

  const { listDetails: listDetailsData = null } = listData?.listDetails?.listResponse || {};
  const listDetails = (listDetailsData || []).filter((list) => list.listId !== skipListId);
  const listsExist = !!listDetails?.length;

  const onCreateList = () => {
    listRefetch();
  };

  const { updateList, updateListResponse } = useUpdateList({
    id: listId,
    operationName: OPERATION_ADD_ITEMS
  });

  const favoriteDetailCount = productData?.favoriteDetail?.count;

  useEffect(() => {
    setFavoriteCount(favoriteDetailCount);
  }, [favoriteDetailCount]);

  const shouldShowSelectList = useMemo(() => {
    return listsExist;
  }, [listLoading]);

  useEffect(() => {
    if (!disableSignInDrawer && !isLoggedIn) {
      setScreen(SIGN_IN);
    } else if (listLoading) {
      setScreen(LOADING);
    } else if (products.length === 0) {
      setScreen(DEFAULT);
    } else if (shouldShowSelectList) {
      setScreen(LIST_SELECT_LIST);
    } else if (screen !== LIST_SUCCESS) {
      setScreen(LIST_CREATE_LIST);
    }
  }, [isLoggedIn, listsExist, listLoading]);

  useEffect(() => {
    if (updateListResponse?.error) {
      setShowErrorMessage(true);
    }
    if (updateListResponse?.data?.updateList?.id) {
      if (onMaterialsAdded) {
        onMaterialsAdded();
      }
      setShowSuccessMessage(true);
    }
  }, [updateListResponse.error, updateListResponse.data]);

  const onSearch = (searchKeyword) => {
    setKeyword(searchKeyword);
  };

  const handleBackPress = () => {
    if (screen === LIST_CREATE_LIST && !!listsExist) {
      setScreen(LIST_SELECT_LIST);
      return;
    }
    if (addToType === "Cart" && !!atcDrawerType) {
      setScreen(MINI_PIP_FULL);
      setAtcDrawerType('');
      return;
    }
    if (screen === MINI_PIP_BASE) {
      setScreen(DEFAULT);
    }
    // eslint-disable-next-line max-len
    if (screen === MINI_PIP_PRODUCT_OVERVIEW || screen === MINI_PIP_PRODUCT_SPECS || screen === MINI_PIP_PRODUCT_REVIEWS) {
      setScreen(MINI_PIP_BASE);
    }
  };

  const showBackButton = !(
    (screen === LIST_SELECT_LIST && products?.length && addToType !== 'Cart')
      || (screen === DEFAULT)
      || (screen === LIST_CREATE_LIST && !listsExist)
  );

  return (
    <>
      {
        !screen?.startsWith('COPY_LIST') && (
          <DrawerHeader
            data-testid="add-to-drawer-header"
            title={SCREEN_TITLES[screen] || 'Add to List'}
            onClose={() => handleClose('close')}
            onBack={showBackButton ? handleBackPress : null}
          />
        )
      }
      {screen === SIGN_IN && (
        <SignIn
          onClose={handleClose}
        />
      )}
      {screen === LOADING && (
        <AddToLoading />
      )}
      {screen === DEFAULT && (
        <DefaultScreen
          keyword={keyword}
          onSearch={onSearch}
          products={products}
          handleClose={handleClose}
          updateList={updateList}
          customerType={customerType}
          setIsSearchResults={setIsSearchResults}
        />
      )}
      {screen === LIST_CREATE_LIST && (
        <CreateList
          onClose={handleClose}
          onCreate={onCreateList}
          products={products}
          isExistingLists={listsExist}
          isSharedList={isSharedList}
          setFavoriteCount={setFavoriteCount}
          favoriteCount={favoriteCount}
          productValue={productValue}
          identifiers={identifiers}
          primaryImage={primaryImage}
          setNewListDetails={setNewListDetails}
        />
      )}
      {screen === LIST_SELECT_LIST && (
        <SelectList
          products={products}
          listDetails={listDetails}
          onClose={handleClose}
          isSharedList={isSharedList}
          setFavoriteCount={setFavoriteCount}
          favoriteCount={favoriteCount}
          productValue={productValue}
          identifiers={identifiers}
          primaryImage={primaryImage}
          setNewListDetails={setNewListDetails}
        />
      )}
      {
        screen?.startsWith('COPY_LIST') && (
          <CopyList
            onClose={handleClose}
            listId={listId}
            onMaterialsAdded={onMaterialsAdded}
          />
        )
      }
      {screen === LIST_SUCCESS && (
        <AddToListSuccess
          newListDetails={newListDetails}
          onClose={handleClose}
        />
      )}
      {
        screen?.startsWith('MINI_PIP') && (
          <MiniPip
            itemId={itemId}
            updateList={updateList}
            isSearchResults={isSearchResults}
          />
        )
      }
    </>
  );
};

AddToList.displayName = 'AddToList';

AddToList.propTypes = {
  isSharedList: PropTypes.bool,
  listId: PropTypes.string,
  onMaterialsAdded: PropTypes.func,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  })),
  skipListId: PropTypes.bool
};

AddToList.defaultProps = {
  isSharedList: false,
  listId: '',
  onMaterialsAdded: () => { },
  products: [],
  skipListId: false
};

const favoriteDetailCount = shape({
  count: number()
});

if (favoriteDetailCount.skip) {
  favoriteDetailCount.skip('skipFavoriteCount', false);
}

AddToList.dataModel = extend(
  {
    product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
      itemId: string(),
      dataSource: string(),
      dataSources: string(),
      favoriteDetail: client(favoriteDetailCount),
      pricing: params({ storeId: string() }).shape({
        value: number({ float: true })
      }),
      identifiers: shape({
        brandName: string(),
        productLabel: string()
      }),
      media: shape({
        images: arrayOf(shape({
          url: string(),
          type: string(),
          subType: string()
        }))
      })
    })
  },
  DefaultScreen,
  CreateList,
  listDetailsModel,
  updateListModel,
  CopyList
);
